import React, { useContext, useEffect, useRef, useState } from 'react';
import { updateLastActivity } from '../../Utils/LastActivity';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import type { JSX } from 'react';
import { ErrorMessage, QuickTextInput } from 'shouldve-been-mui';
import Folder from '../../Models/FolderModel';
import type { IndexedDBContextProps } from '../../Contexts/IndexedDBContext';
import IndexedDBContext from '../../Contexts/IndexedDBContext';
import { shakeForm } from 'shouldve-been-react';

type RenameFolderDialogProps = {
    controller: boolean,
    onClose: () => void,
    folder: Folder,
};

function RenameFolderDialog(props: Readonly<RenameFolderDialogProps>): JSX.Element {

    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);
    const indexedDBContext: IndexedDBContextProps = useContext<IndexedDBContextProps>(IndexedDBContext);

    const formRef: React.RefObject<HTMLFormElement> = useRef<HTMLFormElement>(null);
    
    const [name, setName] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);
    const [rerenderFlag, setRerenderFlag] = useState<Date>(new Date());

    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {
        setName(props.folder.name);
    }, [props.folder.name, rerenderFlag]);

    async function handleSave(): Promise<void> {

        if (name === '') {
            setErrorMessage('Name cannot be empty');
            shakeForm(formRef);
            return;
        }

        const success: boolean = await indexedDBContext.indexedDBService.updateFolder(new Folder(props.folder.folderId, props.folder.templateId, name, props.folder.createdDate, new Date()));

        if (!success) {
            setErrorMessage('Failed to update folder name');
            shakeForm(formRef);
            return;
        }

        handleClose();
    }

    function handleClose(): void {

        updateLastActivity(userContext, localStorageContext);

        setName('');

        props.onClose();
    }

    return (
        <Dialog 
            open={props.controller} 
            onClose={handleClose}
            TransitionProps={{
                onEntered: (): void => setRerenderFlag(new Date()),
            }}
            PaperProps={{
                sx: {
                    position: 'fixed',
                },
            }}
        >
            <DialogTitle>Rename Folder</DialogTitle>
            <DialogContent>
                <form noValidate autoComplete='off' ref={formRef} name='renameFolder'>
                    <QuickTextInput
                        value={name}
                        onBlur={setName}
                        variant='outlined'
                        dense={true}
                        minWidth={Math.min(windowSize[0] * 0.8, 500)}
                        maxWidth={Math.min(windowSize[0] * 0.8, 500)}
                        height='40px'
                    />
                </form>
                <ErrorMessage
                    message={errorMessage}
                    maxWidth={Math.min(windowSize[0] * 0.8, 500)}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                    color='error'
                    variant='contained'
                    style={{
                        width: '90px',
                        height: '32px',
                        lineHeight: 'normal',
                        marginRight: '10px',
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    color='primary'
                    variant='contained'
                    style={{
                        width: '90px',
                        height: '32px',
                        lineHeight: 'normal',
                        marginRight: '10px',
                    }}
                >
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default RenameFolderDialog;